import React from "react";
import { createClasses } from "@bloomberg/media-ui-core";

import Headline from "~components/shared/headline/Headline";
import {
    Media,
    type MediaConfig,
    MediaFormat,
    getWinningMediaFormat,
    AuthorImageMedia,
} from "~components/shared/media/";
import OptionalEyebrow from "~components/shared/optional-eyebrow/OptionalEyebrow";
import PhoenixByline from "~components/shared/phoenix-byline/PhoenixByline";
import { VariantBase } from "~components/shared/variant-base/VariantBase";
import { HEADLINE_SIZE } from "~types/lineup";
import {
    type CuratedStory,
    type VariationComponent,
    ZoneColumnCombo,
} from "~types/types";
import { integrityError } from "~utils/moduleIntegrity";

import integrityCheck from "./LineupContentFeatureLedeWeekend.integrity";
import { PREVIEW_NOTES } from "./previewNotes";
import styles from "./LineupContentFeatureLedeWeekend.module.css";

const SUPPORTED_REGIONS: ZoneColumnCombo[] = [
    ZoneColumnCombo.fullWidthContentWell,
];

const VERTICAL_MEDIA_FORMATS = new Set<MediaFormat>([
    MediaFormat.verticalVideo,
    MediaFormat.loopingVideo,
]);

export const MEDIA_PRIORITY: MediaFormat[] = [
    MediaFormat.verticalVideo,
    MediaFormat.loopingVideo,
    MediaFormat.vodVideo,
    MediaFormat.defaultImage,
];

const MEDIA_CONFIG: MediaConfig = {
    className: styles.media,
    mobileMaxWidth: 715,
};

const DEFAULT_ASPECT_RATIOS: Record<string, number> = {
    landscape: 3 / 2,
    portrait: 2 / 3,
};

function isSquareOrPortrait(width: number, height: number): boolean {
    return height >= width;
}

const LineupContentFeatureLedeWeekend = (props: VariationComponent) => {
    const integrity = integrityCheck(props);

    if (integrity.length) return integrityError(integrity);

    const [item] = props?.items ? props.items : [];
    const { media } = item || {};

    if (!item || !media) return null;

    const winningMediaFormat = getWinningMediaFormat(item, MEDIA_PRIORITY);

    let isLandscape = true;

    if (winningMediaFormat === MediaFormat.defaultImage) {
        const { origWidth = 3, origHeight = 2 } = media.defaultImage || {};

        if (isSquareOrPortrait(origWidth, origHeight)) isLandscape = false;
    }

    if (VERTICAL_MEDIA_FORMATS.has(winningMediaFormat)) isLandscape = false;

    const updatedMediaConfig: MediaConfig = {
        ...MEDIA_CONFIG,
        aspectRatio: isLandscape
            ? DEFAULT_ASPECT_RATIOS.landscape
            : DEFAULT_ASPECT_RATIOS.portrait,
        desktopMaxWidth: isLandscape ? 830 : 565,
    };

    const { franchise, updatedAt, publishedAt } =
        "franchise" in item ? (item as CuratedStory) : {};

    return (
        <VariantBase
            source={props}
            className={createClasses([
                styles.LineupContentFeatureLedeWeekend,
                isLandscape && styles.landscape,
                !isLandscape && styles.portrait,
            ])}
            gridClassName={styles.grid}
            supportedRegions={SUPPORTED_REGIONS}
            previewNotes={PREVIEW_NOTES}
        >
            <Media
                source={item}
                priority={[winningMediaFormat]}
                config={updatedMediaConfig}
            />
            <div className={styles.storyBlock} data-component="story-block">
                <a
                    href={item.url}
                    className={styles.storyLink}
                    data-link-index={1}
                >
                    <OptionalEyebrow
                        label={item.label}
                        franchise={franchise}
                        updatedAt={updatedAt}
                        publishedAt={publishedAt}
                        textLabel={item.textLabel}
                        presetLabel={item.labelDropdown}
                        nicodes={item.nicodes}
                        className={styles.optionalEyebrow}
                    />
                    <Headline
                        text={item.headline}
                        isPhoenix
                        className={styles.headline}
                        size={HEADLINE_SIZE.UNSTYLED}
                    />
                    <AuthorImageMedia
                        authorImage={media.authorImage}
                        config={
                            {
                                desktopMaxWidth: 62,
                                mobileMaxWidth: 62,
                                aspectRatio: 1,
                                className: styles.authorImage,
                            } as MediaConfig
                        }
                    />
                    <PhoenixByline forceShowByline story={item} />
                </a>
            </div>
        </VariantBase>
    );
};

export default LineupContentFeatureLedeWeekend;
